// src/pages/workflow/ApprovalsPage.tsx
// Approvals inbox — everything awaiting me across every pipeline, plus the
// signed-decision audit trail. A person opens this to answer a human task
// (approve / decline / delegate) or to verify that a past decision's signature
// still holds. Cross-pipeline by design: you find approvals without knowing
// which pipeline parked them.

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Badge,
  Textarea,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Alert,
  ErrorAlert,
} from '@react/components';
import { UserPicker } from '@/components/UserPicker';
import { Modal } from '@/features/workflow/Modal';
import {
  getEntityId,
  toBadgeVariant,
  decisionStatusMap,
  decisionVerdictMap,
  formatDateTime,
  formatRelativeTime,
} from '@/features/workflow/logic';

type Row = any;

function unwrapItems(res: any): Row[] {
  return res?.items?.items || res?.items || [];
}

const TABS = [
  { id: 'pending', labelKey: 'workflow.approvals.pending' },
  { id: 'signed', labelKey: 'workflow.approvals.signed' },
] as const;
type ApprovalsTab = typeof TABS[number]['id'];

export const ApprovalsPage = observer(() => {
  const store = useWorkflow();
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get('decision') || undefined;
  const [tab, setTab] = useState<ApprovalsTab>('pending');

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('workflow.approvals.title')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('workflow.approvals.title')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors relative ${
              tab === tb.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(tb.labelKey)}
            {tab === tb.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />}
          </button>
        ))}
      </div>

      {tab === 'pending' ? <PendingTab store={store} t={t} locale={locale} deepLinkId={deepLinkId} /> : <SignedTab store={store} t={t} locale={locale} />}
    </div>
  );
});

// ---------------- Pending decisions ----------------

function PendingTab({ store, t, locale, deepLinkId }: any) {
  const [items, setItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState<Row | null>(null);
  const [delegateOn, setDelegateOn] = useState<Row | null>(null);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListPendingDecisions?.({ limit: 100 })
      .then((res: any) => setItems(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link to a single decision (?decision=<id>) — GetPendingDecision,
  // not a re-query of the list, so a link works even for a decision that has
  // since scrolled off the first page.
  useEffect(() => {
    if (!deepLinkId) return;
    store.GetPendingDecision?.(deepLinkId)
      .then((res: any) => {
        const decision = res?.decision ?? res;
        if (decision) setDecideOn(decision);
        else setDeepLinkError(t('workflow.approvals.decisionNotFound'));
      })
      .catch(() => setDeepLinkError(t('workflow.approvals.decisionNotFound')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId]);

  if (loading) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">{t('workflow.loading')}</CardContent></Card>;
  }

  if (items.length === 0 && !deepLinkError) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p className="text-foreground font-medium">{t('workflow.approvals.emptyTitle')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('workflow.approvals.emptyDesc')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {deepLinkError && <Alert variant="error">{deepLinkError}</Alert>}
      {items.map((d) => {
        const status = d.status || 'DECISION_STATUS_PENDING';
        const info = decisionStatusMap[status];
        return (
          <Card key={getEntityId(d)}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {info && <Badge variant={toBadgeVariant(info.variant)} size="sm">{t(info.labelKey)}</Badge>}
                    {d.execution?.id && (
                      <Link to={`/workflows/executions/${d.execution.id}`} className="font-mono text-xs text-primary hover:underline">
                        {d.execution.id.slice(0, 8)}
                      </Link>
                    )}
                    {d.node_id && <span className="text-xs text-muted-foreground">· {d.node_id}</span>}
                  </div>
                  <p className="text-sm text-foreground">{d.instructions || t('workflow.approvals.noInstructions')}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                    {d.delegated_from?.display_name && <span>{t('workflow.approvals.delegatedFrom')}: {d.delegated_from.display_name}</span>}
                    {d.deadline && <span>{t('workflow.approvals.deadline')}: {formatDateTime(d.deadline, locale)}</span>}
                    {d.created_at && <span>{formatRelativeTime(d.created_at)}</span>}
                  </div>
                </div>
                {status === 'DECISION_STATUS_PENDING' && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="secondary" onClick={() => setDelegateOn(d)}>{t('workflow.approvals.delegate')}</Button>
                    <Button size="sm" onClick={() => setDecideOn(d)}>{t('workflow.approvals.decide')}</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {decideOn && (
        <DecideModal
          decision={decideOn}
          store={store}
          t={t}
          onClose={() => setDecideOn(null)}
          onDone={() => { setDecideOn(null); load(); }}
        />
      )}
      {delegateOn && (
        <DelegateModal
          decision={delegateOn}
          store={store}
          t={t}
          onClose={() => setDelegateOn(null)}
          onDone={() => { setDelegateOn(null); load(); }}
        />
      )}
    </div>
  );
}

function DecideModal({ decision, store, t, onClose, onDone }: any) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const submit = async (verdict: string) => {
    setBusy(verdict);
    try {
      await store.SubmitDecision?.(getEntityId(decision), { verdict: verdict as any, comment: comment || undefined });
      onDone();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('workflow.approvals.decideTitle')}
      description={decision.instructions || undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="ghost" isLoading={busy === 'DECISION_VERDICT_ABSTAINED'} onClick={() => submit('DECISION_VERDICT_ABSTAINED')}>
            {t('workflow.decision.abstained')}
          </Button>
          <Button variant="danger" isLoading={busy === 'DECISION_VERDICT_REJECTED'} onClick={() => submit('DECISION_VERDICT_REJECTED')}>
            {t('workflow.approvals.decline')}
          </Button>
          <Button isLoading={busy === 'DECISION_VERDICT_APPROVED'} onClick={() => submit('DECISION_VERDICT_APPROVED')}>
            {t('workflow.approvals.approve')}
          </Button>
        </>
      }
    >
      <Textarea
        label={t('workflow.approvals.reason')}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        placeholder={t('workflow.approvals.reasonPlaceholder')}
      />
    </Modal>
  );
}

function DelegateModal({ decision, store, t, onClose, onDone }: any) {
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const subject = decision?.subject;
  const documentId = subject?.type_name === 'document' || subject?.type_name === 'core.document' ? subject.id : '';

  const submit = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await store.DelegateDecision?.(getEntityId(decision), {
        delegate_to: { entity: { id: userId, type_name: 'profile' }, display_name: userName } as any,
        comment: comment || undefined,
      });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('workflow.approvals.delegateTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={busy} disabled={!userId}>{t('workflow.approvals.delegate')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <UserPicker value={userId} onChange={(uid, name) => { setUserId(uid); setUserName(name); }} label={t('workflow.approvals.delegateTo')} valueKind="profile" scope={documentId ? { typeName: 'document', id: documentId } : undefined} />
        <Textarea label={t('workflow.approvals.reason')} value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
      </div>
    </Modal>
  );
}

// ---------------- Signed decisions (audit) ----------------

function SignedTab({ store, t, locale }: any) {
  const [items, setItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifyResult, setVerifyResult] = useState<Record<string, { valid: boolean; message: string }>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    store.ListSignedDecisions?.({ limit: 100 })
      .then((res: any) => setItems(unwrapItems(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (sd: Row) => {
    const sid = getEntityId(sd);
    setBusyId(sid);
    try {
      const res = await store.VerifySignedDecision?.(sid, {});
      setVerifyResult((r) => ({ ...r, [sid]: { valid: !!res?.valid, message: res?.message || '' } }));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">{t('workflow.loading')}</CardContent></Card>;
  }

  return (
    <Card>
      <Table hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('workflow.approvals.actor')}</TableHead>
            <TableHead>{t('workflow.approvals.verdict')}</TableHead>
            <TableHead>{t('workflow.approvals.decidedAt')}</TableHead>
            <TableHead>{t('workflow.approvals.signature')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('workflow.approvals.noSigned')} />
          ) : (
            items.map((sd) => {
              const sid = getEntityId(sd);
              const vinfo = decisionVerdictMap[sd.verdict || ''];
              const vr = verifyResult[sid];
              return (
                <TableRow key={sid}>
                  <TableCell className="text-sm">{sd.actor?.display_name || sd.actor?.entity?.id?.slice(0, 8) || '-'}</TableCell>
                  <TableCell>{vinfo ? <Badge variant={toBadgeVariant(vinfo.variant)} size="sm">{t(vinfo.labelKey)}</Badge> : '-'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(sd.decided_at, locale)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{(sd.signature || '').slice(0, 16) || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {vr && (
                        <Badge variant={vr.valid ? 'success' : 'error'} size="sm">
                          {vr.valid ? t('workflow.approvals.verified') : t('workflow.approvals.invalid')}
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" disabled={busyId === sid} onClick={() => verify(sd)}>{t('workflow.approvals.verify')}</Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

export default ApprovalsPage;
